import type { ECharts } from 'echarts';

import type { Trajectory3dChartPoint, Trajectory3dLine } from '../trajectory-graph.store';
import { formatTooltipNumber } from '../utils/format-chart-number';

export type Trajectory3dLineHit = {
  lineIndex: number;
  lineLabel: string;
  point: Trajectory3dChartPoint;
  dataIndex: number;
  screenDistance: number;
};

const MAX_TOOLTIP_LINES = 10;

type Grid3DCamera = {
  projectionMatrix: { array: ArrayLike<number> };
  viewMatrix: { array: ArrayLike<number> };
};

type Grid3DViewport = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type Grid3DCoordSystem = {
  dataToPoint: (data: number[], out?: number[]) => number[];
  viewGL?: {
    camera: Grid3DCamera;
    viewport: Grid3DViewport;
  };
};

type Line3DSeriesModel = { coordinateSystem?: Grid3DCoordSystem };

type Grid3DProjectionContext = {
  coordSys: Grid3DCoordSystem;
  viewport: Grid3DViewport;
  chartHeight: number;
  worldViewProjection: number[];
};

function getLine3DSeriesModels(chart: ECharts): Line3DSeriesModel[] {
  const model = (
    chart as unknown as {
      getModel: () => {
        getSeriesByType?: (type: string) => Line3DSeriesModel[];
      };
    }
  ).getModel();

  return model.getSeriesByType?.('line3D') ?? [];
}

function getGrid3DProjectionContext(chart: ECharts): Grid3DProjectionContext | null {
  const coordSys = getLine3DSeriesModels(chart)[0]?.coordinateSystem;
  const viewport = coordSys?.viewGL?.viewport;
  const camera = coordSys?.viewGL?.camera;

  if (!coordSys || !viewport || !camera) {
    return null;
  }

  return {
    coordSys,
    viewport,
    chartHeight: chart.getHeight(),
    worldViewProjection: multiplyMat4(camera.projectionMatrix.array, camera.viewMatrix.array),
  };
}

/**
 * `line3D` series ignore the `silent` option in echarts-gl: their mesh is always
 * pickable and dispatches its own (single-nearest, x-only) axis pointer via
 * `Line3DView`'s built-in mouse handler. That competes with our own screen-space,
 * multi-line hit test below, causing flaky/incorrect hover behaviour.
 *
 * Reaching into `ignorePicking` on the underlying mesh is the only way to fully
 * opt a `line3D` series out of native picking. Must be re-applied after every
 * `setOption` call, since a fresh mesh can be created internally.
 */
export function disableNativeLine3DPicking(chart: ECharts): void {
  const chartWithViews = chart as unknown as {
    getViewOfSeriesModel?: (seriesModel: unknown) => { _line3DMesh?: { ignorePicking?: boolean } } | undefined;
  };

  getLine3DSeriesModels(chart).forEach((seriesModel) => {
    const view = chartWithViews.getViewOfSeriesModel?.(seriesModel);

    if (view?._line3DMesh) {
      view._line3DMesh.ignorePicking = true;
    }
  });
}

function multiplyMat4(a: ArrayLike<number>, b: ArrayLike<number>): number[] {
  const out = new Array<number>(16).fill(0);

  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      let sum = 0;

      for (let index = 0; index < 4; index += 1) {
        sum += a[row + index * 4] * b[index + col * 4];
      }

      out[row + col * 4] = sum;
    }
  }

  return out;
}

/** Same viewport-local space as echarts-gl Line3DView._pick. */
function projectWorldPointToPickSpace(
  worldPoint: [number, number, number],
  worldViewProjection: number[],
  viewport: Grid3DViewport,
): [number, number] | null {
  const [x, y, z] = worldPoint;
  const clipX =
    worldViewProjection[0] * x + worldViewProjection[4] * y + worldViewProjection[8] * z + worldViewProjection[12];
  const clipY =
    worldViewProjection[1] * x + worldViewProjection[5] * y + worldViewProjection[9] * z + worldViewProjection[13];
  const clipW =
    worldViewProjection[3] * x + worldViewProjection[7] * y + worldViewProjection[11] * z + worldViewProjection[15];

  if (Math.abs(clipW) < 1e-8) {
    return null;
  }

  const ndcX = clipX / clipW;
  const ndcY = clipY / clipW;
  const halfWidth = viewport.width * 0.5;
  const halfHeight = viewport.height * 0.5;

  return [(ndcX + 1) * halfWidth, (ndcY + 1) * halfHeight];
}

/** Convert zrender mouse coords to the same viewport-local space as Line3DView._pick. */
function zrMouseToPickSpace(
  mouseX: number,
  mouseY: number,
  viewport: Grid3DViewport,
  chartHeight: number,
): [number, number] {
  const flippedY = chartHeight - mouseY;

  return [mouseX - viewport.x, flippedY - viewport.y];
}

function distancePointToSegment(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;

  if (dx === 0 && dy === 0) {
    return Math.hypot(px - x0, py - y0);
  }

  const t = Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / (dx * dx + dy * dy)));
  const projX = x0 + t * dx;
  const projY = y0 + t * dy;

  return Math.hypot(px - projX, py - projY);
}

function findClosestPointOnLine(
  context: Grid3DProjectionContext,
  line: Trajectory3dLine,
  pickMouseX: number,
  pickMouseY: number,
  hitRadius: number,
): Omit<Trajectory3dLineHit, 'lineIndex' | 'lineLabel'> | null {
  if (line.data.length === 0) {
    return null;
  }

  let bestDistance = Number.POSITIVE_INFINITY;
  let bestDataIndex = 0;

  for (let index = 1; index < line.data.length; index += 1) {
    const startWorld = context.coordSys.dataToPoint([...line.data[index - 1]]) as [number, number, number];
    const endWorld = context.coordSys.dataToPoint([...line.data[index]]) as [number, number, number];
    const start = projectWorldPointToPickSpace(startWorld, context.worldViewProjection, context.viewport);
    const end = projectWorldPointToPickSpace(endWorld, context.worldViewProjection, context.viewport);

    if (!start || !end) {
      continue;
    }

    const distance = distancePointToSegment(pickMouseX, pickMouseY, start[0], start[1], end[0], end[1]);

    if (distance > hitRadius || distance >= bestDistance) {
      continue;
    }

    const distToStart = Math.hypot(pickMouseX - start[0], pickMouseY - start[1]);
    const distToEnd = Math.hypot(pickMouseX - end[0], pickMouseY - end[1]);
    bestDistance = distance;
    bestDataIndex = distToStart <= distToEnd ? index - 1 : index;
  }

  if (!Number.isFinite(bestDistance)) {
    const singleWorld = context.coordSys.dataToPoint([...line.data[0]]) as [number, number, number];
    const singlePoint = projectWorldPointToPickSpace(singleWorld, context.worldViewProjection, context.viewport);

    if (!singlePoint) {
      return null;
    }

    const distance = Math.hypot(pickMouseX - singlePoint[0], pickMouseY - singlePoint[1]);

    if (distance > hitRadius) {
      return null;
    }

    return {
      point: line.data[0],
      dataIndex: 0,
      screenDistance: distance,
    };
  }

  return {
    point: line.data[bestDataIndex],
    dataIndex: bestDataIndex,
    screenDistance: bestDistance,
  };
}

/**
 * Finds all trajectory lines whose on-screen projection passes within `hitRadius`
 * screen pixels of the cursor — i.e. a virtual circle of that radius is drawn
 * around the cursor, and every line intersecting it is reported, not just the
 * closest one. Distance is measured purely in 2D screen space (after 3D->2D
 * projection), so lines that are far apart in 3D but happen to look close
 * together from the current camera angle are still both matched.
 */
export function findNearbyTrajectoryLineHits(
  chart: ECharts,
  lines: Trajectory3dLine[],
  mouseX: number,
  mouseY: number,
  hitRadius: number,
): Trajectory3dLineHit[] {
  const context = getGrid3DProjectionContext(chart);

  if (!context) {
    return [];
  }

  const [pickMouseX, pickMouseY] = zrMouseToPickSpace(mouseX, mouseY, context.viewport, context.chartHeight);
  const hits: Trajectory3dLineHit[] = [];

  lines.forEach((line, lineIndex) => {
    const closestPoint = findClosestPointOnLine(context, line, pickMouseX, pickMouseY, hitRadius);

    if (!closestPoint) {
      return;
    }

    hits.push({
      lineIndex,
      lineLabel: line.label,
      ...closestPoint,
    });
  });

  return hits.sort((a, b) => a.screenDistance - b.screenDistance).slice(0, MAX_TOOLTIP_LINES);
}

export type Trajectory3dAxisLabels = {
  we: string;
  ns: string;
  tvd: string;
};

export function formatTrajectory3dTooltipHtml(hits: Trajectory3dLineHit[], labels: Trajectory3dAxisLabels): string {
  if (!hits.length) {
    return '';
  }

  return hits
    .map(({ lineLabel, point: [we, ns, depth] }) => {
      return [
        `${lineLabel}`,
        `${labels.we}: ${formatTooltipNumber(we)}`,
        `${labels.ns}: ${formatTooltipNumber(ns)}`,
        `${labels.tvd}: ${formatTooltipNumber(-depth)}`,
      ].join('<br/>');
    })
    .join('<br/><br/>');
}

export function showAxisPointerForClosestHit(chart: ECharts, hits: Trajectory3dLineHit[]): void {
  if (!hits.length) {
    chart.dispatchAction({ type: 'grid3DHideAxisPointer' });
    return;
  }

  const [we, ns, depth] = hits[0].point;

  chart.dispatchAction({
    type: 'grid3DShowAxisPointer',
    value: [we, ns, depth],
  });
}
